import { useCallback, useState, type ReactNode } from "react";

/** A transient banner shown after an admin action. */
export interface AdminStatus {
  kind: "success" | "error";
  message: string;
}

/** Small hook for the success/error banner shared by every admin view. */
export function useAdminStatus(): {
  status: AdminStatus | null;
  reportSuccess: (message: string) => void;
  reportError: (message: string) => void;
  clearStatus: () => void;
} {
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const reportSuccess = useCallback(
    (message: string) => setStatus({ kind: "success", message }),
    [],
  );
  const reportError = useCallback(
    (message: string) => setStatus({ kind: "error", message }),
    [],
  );
  const clearStatus = useCallback(() => setStatus(null), []);
  return { status, reportSuccess, reportError, clearStatus };
}

interface AdminPanelProps {
  title: string;
  description?: string;
  status: AdminStatus | null;
  children: ReactNode;
}

/**
 * Shared scaffold for an admin view: a heading block, an optional status
 * banner, and the view's controls. Keeps spacing and the success/error
 * presentation uniform across all five management views.
 */
export function AdminPanel({
  title,
  description,
  status,
  children,
}: AdminPanelProps): JSX.Element {
  return (
    <section className="admin-panel" aria-label={title}>
      <header className="admin-panel__head">
        <h2 className="admin-panel__title">{title}</h2>
        {description ? (
          <p className="admin-panel__description">{description}</p>
        ) : null}
      </header>
      {status ? (
        <p
          className={`admin-status admin-status--${status.kind}`}
          role={status.kind === "error" ? "alert" : "status"}
        >
          {status.message}
        </p>
      ) : null}
      {children}
    </section>
  );
}
